'use client';

import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';

import { getGraphQLUrl } from './config';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

function readStoredAccessToken(): string | null {
  if (typeof window === 'undefined') return null;

  // Backward compatibility for any old single-token storage.
  const legacyToken = localStorage.getItem('authToken');
  if (legacyToken) return legacyToken;

  const rawTokens = localStorage.getItem('auth_tokens');
  if (!rawTokens) return null;

  try {
    const parsed = JSON.parse(rawTokens) as {
      access_token?: string;
      accessToken?: string;
      token?: string;
    };
    return parsed.access_token ?? parsed.accessToken ?? parsed.token ?? null;
  } catch {
    return null;
  }
}

/**
 * HTTP Link for Apollo Client
 */
const httpLink = createHttpLink({
  uri: getGraphQLUrl(),
});

/**
 * Error Link - Handles GraphQL errors
 */
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

/**
 * Auth Link - Adds authentication headers if needed
 * Modify this to add your authentication token
 */
const authLink = setContext((_, { headers }) => {
  const token = readStoredAccessToken();
  const existingAuthorization = headers?.authorization;
  const authorizationHeader = existingAuthorization || (token ? `Bearer ${token}` : '');

  return {
    headers: {
      ...headers,
      authorization: authorizationHeader,
      // Add any other headers you need
      'Content-Type': 'application/json',
    },
  };
});

/**
 * Create Apollo Client instance
 */
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    // Add any cache configuration here
    typePolicies: {
      // Example: Custom cache policies for specific types
      // Query: {
      //   fields: {
      //     // Add field policies here
      //   },
      // },
    },
  }),
  // Enable Apollo DevTools in development
  connectToDevTools: process.env.NODE_ENV === 'development',
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

